import { ChevronRight } from 'lucide-react';
import { experiences, primaryImage, type Experience } from '../../../data/experiences';
import { useExperiences } from '../../../hooks';
import { imgDims } from '../../../data/imageMeta';
import { formatPrice } from '../format';
import { WhatsAppFallback } from '../WhatsAppFallback';

export function ExperienceStep({
  onSelect,
}: {
  onSelect: (experience: Experience) => void;
}) {
  const { byAlias, status } = useExperiences();

  // No static price fallback: if the engine is down, send guests to WhatsApp.
  if (status === 'error') return <WhatsAppFallback />;

  return (
    <div>
      <h3 className="font-display text-2xl font-medium text-charcoal">
        Choose your experience
      </h3>
      <p className="mt-1.5 font-sans text-sm font-light text-stone">
        Pick the experience you&rsquo;d like to book.
      </p>

      <ul className="mt-6 flex flex-col gap-3">
        {experiences.map((exp) => {
          const dyn = byAlias(exp.productAlias);
          const from = dyn?.startingDiscountedPrice ?? dyn?.startingPrice ?? null;
          const image = primaryImage(exp);
          const { width, height } = imgDims(image);
          return (
            <li key={exp.id}>
              <button
                type="button"
                onClick={() => onSelect(exp)}
                className="group flex w-full items-center gap-4 rounded-xl border border-charcoal/15 bg-white p-3 text-left transition-colors hover:border-crimson hover:bg-cream-soft"
              >
                <img
                  src={image}
                  alt=""
                  width={width}
                  height={height}
                  loading="lazy"
                  className="h-16 w-20 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-sans text-[0.58rem] font-semibold uppercase tracking-label text-crimson">
                    {exp.kicker}
                  </p>
                  <p className="truncate font-display text-lg font-medium text-charcoal">
                    {exp.title}
                  </p>
                  {from != null && (
                    <p className="font-sans text-[0.7rem] font-medium uppercase tracking-wide2 text-charcoal/55">
                      from{' '}
                      <span className="font-display text-sm normal-case tracking-normal text-charcoal">
                        {formatPrice(from)}
                      </span>
                    </p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-charcoal/40 transition-transform group-hover:translate-x-0.5 group-hover:text-crimson" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
